/**
 * ActivityStrip — bottom strip on the Posture tab.
 *
 * Visual reference: 04-hybrid-recommended.html .strip block.
 * Two columns: recent activity feed (timestamped, pillar-colored badges)
 * and a quick-actions panel of one-click follow-ups.
 *
 * Activity data: DashboardStats.recentActivity (existing endpoint).
 * Quick actions: navigation links + the existing scan trigger from QuickActions.
 */

import { format } from 'date-fns'
import { useNavigate } from 'react-router-dom'
import {
  ArrowRightIcon,
  ArrowPathIcon,
  BoltIcon,
  ClipboardDocumentCheckIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline'
import type { ActivityItem, DashboardStats } from '@/types'

interface Props {
  stats: DashboardStats | null | undefined
  isLoading: boolean
  /** Optional callback used by the "Run full evaluation" quick action. */
  onRunEvaluation?: () => void
  /** When true, disables the "Run full evaluation" action and shows a spinner. */
  isRunningEvaluation?: boolean
}

/** Map activity event types to a pillar badge tone. */
function pillarFor(type: ActivityItem['type']): 'compliance' | 'bpa' | 'identity' {
  if (
    type === 'drift_detected' ||
    type === 'drift_resolved' ||
    type === 'drift_approved_by_user' ||
    type === 'drift_rejected_by_user' ||
    type === 'drift_remediated' ||
    type === 'compliance_scan' ||
    type === 'compliance_scan_failed' ||
    type === 'compliance_scan_partial' ||
    type === 'baseline_refresh' ||
    type === 'baseline_refresh_failed' ||
    type === 'baseline_refresh_partial'
  ) {
    return 'compliance'
  }
  if (
    type === 'scuba_scan' ||
    type === 'scuba_scan_failed' ||
    type === 'scuba_scan_partial' ||
    type === 'bpa_scan' ||
    type === 'bpa_scan_failed' ||
    type === 'bpa_scan_partial'
  ) {
    return 'bpa'
  }
  // App Posture events (OAuth app + consent sync) — identity-adjacent in
  // posture model, so they pick up the identity tone. Falls through here
  // alongside any future identity-pillar event types.
  return 'identity'
}

const BADGE_CLASS = {
  compliance: 'bg-primary-500/20 text-primary-700 dark:text-primary-400',
  bpa: 'bg-secondaryBlue-300/30 text-secondaryBlue-700 dark:text-secondaryBlue-300',
  identity: 'bg-secondarySand-300/40 text-secondarySand-700 dark:text-secondarySand-300',
} as const

const BADGE_LABEL = {
  compliance: 'Compliance',
  bpa: 'Best Practice',
  identity: 'Identity',
} as const

export default function ActivityStrip({
  stats,
  isLoading,
  onRunEvaluation,
  isRunningEvaluation = false,
}: Props) {
  const navigate = useNavigate()
  const events = (stats?.recentActivity ?? []).slice(0, 5)

  return (
    <section
      aria-label="Recent activity and quick actions"
      className="grid gap-6 lg:grid-cols-[2fr_1fr]"
    >
      <div
        className="
          rounded-2xl border border-dark-700 bg-dark-800 px-8 py-7
          shadow-[0_1px_2px_rgba(33,41,56,0.04),0_8px_24px_rgba(33,41,56,0.04)]
        "
      >
        <h4 className="mb-4 flex items-center justify-between text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500">
          Recent activity
          {events.length > 0 && (
            <span className="rounded-full bg-[#212938] px-2.5 py-0.5 text-[11px] font-semibold tracking-normal text-white">
              {events.length}
            </span>
          )}
        </h4>

        {isLoading ? (
          <div className="flex animate-pulse flex-col gap-3">
            {[0, 1, 2, 3].map(i => (
              <div key={i} className="h-9 rounded bg-dark-700" />
            ))}
          </div>
        ) : events.length === 0 ? (
          <p className="py-3 text-[13px] text-slate-500">
            No activity in the last 24 hours. Trigger a scan from the panel on the right.
          </p>
        ) : (
          <ul className="flex flex-col gap-3">
            {events.map(event => {
              const tone = pillarFor(event.type)
              return (
                <li
                  key={event.id}
                  className="
                    grid grid-cols-[64px_1fr_auto] items-center gap-4 border-b border-dashed border-dark-700 pb-3
                    last:border-b-0 last:pb-0
                  "
                >
                  <span className="font-mono text-[11px] font-bold tabular-nums text-slate-500">
                    {format(new Date(event.timestamp), 'HH:mm')}
                  </span>
                  <p className="text-[13px] text-slate-100 dark:text-slate-100">
                    <strong className="font-bold">{event.title}</strong>
                    {event.description ? ` — ${event.description}` : ''}
                  </p>
                  <span
                    className={`whitespace-nowrap rounded-[4px] px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.12em] ${BADGE_CLASS[tone]}`}
                  >
                    {BADGE_LABEL[tone]}
                  </span>
                </li>
              )
            })}
          </ul>
        )}
      </div>

      <div
        className="
          rounded-2xl border border-dark-700 bg-dark-800 px-8 py-7
          shadow-[0_1px_2px_rgba(33,41,56,0.04),0_8px_24px_rgba(33,41,56,0.04)]
        "
      >
        <h4 className="mb-4 text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500">
          Quick actions
        </h4>
        <div className="flex flex-col gap-2.5">
          <QuickItem
            Icon={isRunningEvaluation ? ArrowPathIcon : BoltIcon}
            iconSpin={isRunningEvaluation}
            title={isRunningEvaluation ? 'Running full evaluation…' : 'Run full evaluation'}
            meta="Baselines, best-practice, identity, and apps in one pass"
            onClick={onRunEvaluation}
            disabled={isRunningEvaluation || !onRunEvaluation}
          />
          <QuickItem
            Icon={ClipboardDocumentCheckIcon}
            title="Export executive PDF"
            meta="Posture brief for stakeholders"
            onClick={() => navigate('/executive-report')}
          />
          <QuickItem
            Icon={ArrowPathIcon}
            title="Refresh baseline"
            meta="M365DSC · trigger a fresh capture"
            onClick={() => navigate('/configuration-drift')}
          />
          <QuickItem
            Icon={ShieldCheckIcon}
            title="Open Security Center"
            meta="Review BPA framework violations"
            onClick={() => navigate('/security')}
          />
        </div>
      </div>
    </section>
  )
}

function QuickItem({
  Icon,
  iconSpin = false,
  title,
  meta,
  onClick,
  disabled = false,
}: {
  Icon: typeof BoltIcon
  iconSpin?: boolean
  title: string
  meta: string
  onClick?: () => void
  disabled?: boolean
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="
        group grid grid-cols-[32px_1fr_auto] items-center gap-3.5
        rounded-xl border border-dark-700 px-3.5 py-3 text-left
        text-slate-100 transition-all duration-150
        enabled:hover:-translate-y-px enabled:hover:border-primary-500 enabled:hover:bg-primary-500/[0.04]
        disabled:cursor-not-allowed disabled:opacity-60
        dark:text-slate-100
      "
    >
      <span className="grid h-8 w-8 place-items-center rounded-lg bg-dark-900">
        <Icon
          className={`h-4 w-4 text-slate-500 group-enabled:group-hover:text-primary-500 dark:group-enabled:group-hover:text-primary-400 ${
            iconSpin ? 'animate-spin' : ''
          }`}
        />
      </span>
      <span className="flex flex-col">
        <span className="text-[13px] font-semibold leading-tight">{title}</span>
        <span className="mt-0.5 text-[11px] font-medium text-slate-500">{meta}</span>
      </span>
      <ArrowRightIcon className="h-4 w-4 text-slate-500 transition-transform group-enabled:group-hover:translate-x-1 group-enabled:group-hover:text-slate-100" />
    </button>
  )
}
